import { defineField, defineType } from "sanity";

export const customRequestPage = defineType({
  name: "customRequestPage",
  title: "Custom Request Page",
  type: "document",
  groups: [
    { name: "header", title: "Header", default: true },
    { name: "form", title: "Form Labels" },
    { name: "success", title: "Success & Errors" },
    { name: "seo", title: "SEO" }
  ],
  fields: [
    // ── Header ─────────────────────────────────────────────────────────
    defineField({
      name: "eyebrow",
      title: "Eyebrow text",
      type: "string",
      description: "Small uppercase text above the heading (e.g. 'If you can dream it, we can make it').",
      group: "header"
    }),
    defineField({
      name: "heading",
      title: "Page heading",
      type: "string",
      description: "Main heading for the page (e.g. 'Custom Request').",
      group: "header"
    }),
    defineField({
      name: "description",
      title: "Page description",
      type: "text",
      rows: 3,
      description: "Supporting copy shown below the heading.",
      group: "header"
    }),

    // ── Form Labels ────────────────────────────────────────────────────
    defineField({
      name: "referencePieceLabel",
      title: "Reference Piece label",
      type: "string",
      description: "Label for the selected reference piece section.",
      group: "form"
    }),
    defineField({
      name: "pieceTypeLabel",
      title: "Piece Type label",
      type: "string",
      description: "Label for the piece type selector (e.g. 'Type of Piece').",
      group: "form"
    }),
    defineField({
      name: "pieceTypeAutoDetectedSuffix",
      title: "Piece Type auto-detected suffix",
      type: "string",
      description: "Text appended when a reference piece is selected (e.g. '(auto-detected)').",
      group: "form"
    }),
    defineField({
      name: "descriptionLabel",
      title: "Description label",
      type: "string",
      description: "Label for the description textarea (e.g. 'Tell Us About Your Dream Piece').",
      group: "form"
    }),
    defineField({
      name: "descriptionPlaceholder",
      title: "Description placeholder",
      type: "text",
      rows: 2,
      description: "Placeholder text inside the description textarea.",
      group: "form"
    }),
    defineField({
      name: "goldColorLabel",
      title: "Gold Color label",
      type: "string",
      description: "Label for the gold color selector.",
      group: "form"
    }),
    defineField({
      name: "stonePreferenceLabel",
      title: "Stone Preference label",
      type: "string",
      description: "Label for the stone preference section.",
      group: "form"
    }),
    defineField({
      name: "byStoneNameLabel",
      title: "By Stone Name button label",
      type: "string",
      description: "Label for the 'By Stone Name' toggle button.",
      group: "form"
    }),
    defineField({
      name: "byColorLabel",
      title: "By Color button label",
      type: "string",
      description: "Label for the 'By Color' toggle button.",
      group: "form"
    }),
    defineField({
      name: "ringSizeLabel",
      title: "Ring Size label",
      type: "string",
      description: "Label for the ring size selector (e.g. 'Ring Size (US)').",
      group: "form"
    }),
    defineField({
      name: "necklaceLengthLabel",
      title: "Necklace Length label",
      type: "string",
      description: "Label for the necklace length selector.",
      group: "form"
    }),
    defineField({
      name: "braceletLengthLabel",
      title: "Bracelet Length label",
      type: "string",
      description: "Label for the bracelet length selector.",
      group: "form"
    }),
    defineField({
      name: "contactInfoLabel",
      title: "Contact Information label",
      type: "string",
      description: "Label for the contact information section.",
      group: "form"
    }),
    defineField({
      name: "namePlaceholder",
      title: "Name input placeholder",
      type: "string",
      description: "Placeholder text for the name input.",
      group: "form"
    }),
    defineField({
      name: "emailPlaceholder",
      title: "Email input placeholder",
      type: "string",
      description: "Placeholder text for the email input.",
      group: "form"
    }),
    defineField({
      name: "submitButtonText",
      title: "Submit button text",
      type: "string",
      description: "Text on the submit button (e.g. 'Submit Your Request').",
      group: "form"
    }),
    defineField({
      name: "submittingButtonText",
      title: "Submitting button text",
      type: "string",
      description: "Text shown while the form is submitting (e.g. 'Submitting...').",
      group: "form"
    }),
    defineField({
      name: "footerNote",
      title: "Footer note",
      type: "string",
      description: "Small text shown below the form (e.g. 'We\\'ll review your request and get back to you within 48 hours').",
      group: "form"
    }),

    // ── Success & Errors ───────────────────────────────────────────────
    defineField({
      name: "successHeading",
      title: "Success heading",
      type: "string",
      description: "Heading shown after successful submission (e.g. 'Thank You').",
      group: "success"
    }),
    defineField({
      name: "successMessage",
      title: "Success message",
      type: "text",
      rows: 3,
      description: "Message shown after successful submission.",
      group: "success"
    }),
    defineField({
      name: "successButtonText",
      title: "Success button text",
      type: "string",
      description: "Text on the button shown after success (e.g. 'Continue Browsing').",
      group: "success"
    }),
    defineField({
      name: "successButtonLink",
      title: "Success button link",
      type: "string",
      description: "URL the success button navigates to (e.g. '/browse').",
      group: "success"
    }),
    defineField({
      name: "errorEmailRequired",
      title: "Email required error",
      type: "string",
      description: "Error shown when the email field is empty.",
      group: "success"
    }),
    defineField({
      name: "errorNameRequired",
      title: "Name required error",
      type: "string",
      description: "Error shown when the name field is empty.",
      group: "success"
    }),
    defineField({
      name: "errorGeneric",
      title: "Generic error message",
      type: "string",
      description: "Fallback error message for unexpected failures.",
      group: "success"
    }),

    // ── SEO ────────────────────────────────────────────────────────────
    defineField({
      name: "metaTitle",
      title: "Meta title",
      type: "string",
      description: "Page title for search engines (e.g. 'Custom Request | Vault 88').",
      group: "seo"
    }),
    defineField({
      name: "metaDescription",
      title: "Meta description",
      type: "text",
      rows: 3,
      description: "Short description for search engines.",
      group: "seo"
    }),
    defineField({
      name: "ogImage",
      title: "Sharing image",
      type: "image",
      description: "Image shown when this page is shared on social media.",
      options: { hotspot: true },
      fields: [
        defineField({
          name: "alt",
          title: "Alt text",
          type: "string",
          description: "Describe the image for screen readers."
        })
      ],
      group: "seo"
    })
  ],
  preview: {
    prepare: () => ({
      title: "Custom Request Page"
    })
  }
});
